/**
 * The map assets' generated sprites (`projects/map-assets.md` 3.5, `decisions/0193` point 7 as
 * amended 2026-10-01): the art in `public/map-assets/`, intact and damaged per variant, drawn into
 * the same footprint-plus-margin canvas the painter (`assetArt.ts`) fills, so the layer places
 * either the same way. The scale and seal trees are developer-approved; the session-4 set
 * (`projects/session-4-maps.md`) awaits that eyeball. The trunk, the horsetail and the wreck keep
 * the painter.
 *
 * The sprites load on the first sprite-drawn asset the board asks for and never block it: until
 * they land, the asset draws with the painter, and the layer is told to redraw when they do.
 */
import { CELL } from "../combatCells";
import { ASSET_MARGIN, assetCanvasSide } from "./assetArt";
import type { AssetLook } from "./assetLook";

/** Where the sprites are served from. */
export const TREE_SPRITE_DIR = "/map-assets";

/**
 * The variant letters on disk per sprite-drawn asset. The scale tree has no `b` (dropped at the
 * spike), nor the ore cart (its `b` came back side-on). The five after the trees are the
 * session-4 set (`projects/session-4-maps.md`), generated the same way. The reactor and the
 * printers are the foundry rebuild's (`projects/foundry-rebuild.md`); the three big printers are
 * one drawing each (empty, and with each brother's mech), drawn the same way up for every seed.
 */
const LETTERS = {
  "scale-tree": ["a", "c"],
  "seal-tree": ["a", "b", "c"],
  "standing-dead": ["a", "b", "c"],
  rockfall: ["a", "b", "c"],
  smelter: ["a", "b", "c"],
  "mech-cradle": ["a", "b", "c"],
  "ore-cart": ["a", "c"],
  reactor: ["a", "b", "c"],
  "mech-printer": ["a"],
  "mech-printer-cataphract": ["a"],
  "mech-printer-everest": ["a"],
  "mech-printer-small": ["a", "b", "c"],
} as const;

type Tree = keyof typeof LETTERS;

const isTree = (id: string): id is Tree => id in LETTERS;

/** Every sprite file, without its extension. */
export const TREE_SPRITE_FILES: readonly string[] = Object.entries(LETTERS).flatMap(([tree, letters]) =>
  letters.flatMap((v) => [`${tree}-${v}-intact`, `${tree}-${v}-damaged`]),
);

/** Which file draws a look, turned by `turn` quarter turns and mirrored left to right when `mirror`. */
export type TreeArt = { file: string; turn: 0 | 1 | 2 | 3; mirror: boolean };

/**
 * The art for each of the `ASSET_VARIANTS` seeds: the files in order, then the first ones
 * again, turned and mirrored, so the four variants are four distinct drawings.
 */
function variant(tree: Tree, seed: number): Omit<TreeArt, "file"> & { letter: string } {
  const letters = LETTERS[tree];
  // A one-drawing asset (a big printer, its mech upright on the bed) is never turned or mirrored.
  if (letters.length === 1) return { letter: letters[0]!, turn: 0, mirror: false };
  if (seed < letters.length) return { letter: letters[seed]!, turn: 0, mirror: false };
  const k = seed - letters.length;
  return { letter: letters[k % letters.length]!, turn: k % 2 === 0 ? 2 : 1, mirror: true };
}

/** The sprite for `look`, or null for an asset the painter draws. A destroyed tree still placed draws damaged. */
export function treeArt(look: AssetLook): TreeArt | null {
  if (!isTree(look.assetId)) return null;
  const { letter, turn, mirror } = variant(look.assetId, look.seed);
  return { file: `${look.assetId}-${letter}-${look.band === "intact" ? "intact" : "damaged"}`, turn, mirror };
}

/** The loaded sprites, by file. */
export type TreeSprites = ReadonlyMap<string, ImageBitmap>;

/** Fetch and decode every tree sprite. */
export async function loadTreeSprites(base = ""): Promise<TreeSprites> {
  const loaded = await Promise.all(
    TREE_SPRITE_FILES.map(async (file) => {
      const res = await fetch(`${base}${TREE_SPRITE_DIR}/${file}.webp`);
      if (!res.ok) throw new Error(`tree sprite ${file}: HTTP ${res.status}`);
      return [file, await createImageBitmap(await res.blob())] as const;
    }),
  );
  return new Map(loaded);
}

let shared: Promise<TreeSprites> | null = null;

/** The one sprite load every board shares. A failed fetch is retried on the next call. */
export function sharedTreeSprites(): Promise<TreeSprites> {
  if (!shared) {
    shared = loadTreeSprites();
    shared.catch(() => (shared = null));
  }
  return shared;
}

/** The sprite's side as a share of the footprint's, as in the approved composite. */
const FILL = 0.96;

/** The soft shadow, in board px: cast south-east like the painter's, and well inside {@link ASSET_MARGIN}. */
const SHADOW = { dx: CELL * 0.12, dy: CELL * 0.12, blur: CELL * 0.08, color: "rgba(10,12,6,0.45)" };

const CANVASES = new WeakMap<TreeSprites, Map<string, OffscreenCanvas>>();

/**
 * The drawing for `look` from the sprites, or null when the painter draws it: not a tree, or the
 * sprites haven't landed. Cached per sprite load like the painter's.
 */
export function treeSprite(look: AssetLook, sprites: TreeSprites | null, resolution: number): OffscreenCanvas | null {
  const art = treeArt(look);
  const image = art && sprites?.get(art.file);
  if (!art || !image || !sprites) return null;
  let cache = CANVASES.get(sprites);
  if (!cache) CANVASES.set(sprites, (cache = new Map()));
  const key = `${look.cells}|${art.file}|${art.turn}|${+art.mirror}|${resolution}`;
  let canvas = cache.get(key);
  if (!canvas) {
    const side = assetCanvasSide(look, resolution);
    canvas = new OffscreenCanvas(side, side);
    const g = canvas.getContext("2d")!;
    const foot = look.cells * CELL;
    const inner = foot * FILL;
    g.setTransform(resolution, 0, 0, resolution, (ASSET_MARGIN + foot / 2) * resolution, (ASSET_MARGIN + foot / 2) * resolution);
    g.rotate((art.turn * Math.PI) / 2);
    if (art.mirror) g.scale(-1, 1);
    // Shadow offsets and blur are in device pixels, untouched by the turn above.
    g.shadowColor = SHADOW.color;
    g.shadowBlur = SHADOW.blur * resolution;
    g.shadowOffsetX = SHADOW.dx * resolution;
    g.shadowOffsetY = SHADOW.dy * resolution;
    g.imageSmoothingQuality = "high";
    g.drawImage(image, -inner / 2, -inner / 2, inner, inner);
    cache.set(key, canvas);
  }
  return canvas;
}
